import React, { useState, useRef, useEffect } from 'react';
import { useReactToPrint } from 'react-to-print';
import  jsPDF  from 'jspdf';
import html2canvas from 'html2canvas';
import { v4 as uuidv4 } from 'uuid';
import './InvoiceSystem.css'; // Optional styling

const InvoiceSystem = () => {
  const componentRef = useRef();
  const [invoices, setInvoices] = useState([]);
  const [currentInvoice, setCurrentInvoice] = useState({
    id: '',
    invoiceNumber: '',
    date: new Date().toISOString().split('T')[0],
    dueDate: '',
    billTo: { name: '', address: '', email: '', phone: '' },
    items: [{ id: uuidv4(), description: '', quantity: 1, price: 0, total: 0 }],
    notes: '',
    subtotal: 0,
    tax: 0,
    total: 0,
  });
  const [isEdit, setIsEdit] = useState(false);

  // Load invoices from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('invoices');
    if (saved) setInvoices(JSON.parse(saved));
  }, []);

  // Save invoices to localStorage
  useEffect(() => {
    localStorage.setItem('invoices', JSON.stringify(invoices));
  }, [invoices]);

  // Auto-generate invoice number
  useEffect(() => {
    if (!isEdit && !currentInvoice.invoiceNumber) {
      const nextNum = `INV-${String(invoices.length + 1).padStart(4, '0')}`;
      setCurrentInvoice(prev => ({ ...prev, invoiceNumber: nextNum }));
    }
  }, [invoices.length, isEdit]);

  // Calculate totals
  useEffect(() => {
    const subtotal = currentInvoice.items.reduce((sum, item) => sum + item.total, 0);
    const tax = subtotal * (currentInvoice.tax / 100);
    const total = subtotal + tax;

    setCurrentInvoice(prev => ({ ...prev, subtotal, total }));
  }, [currentInvoice.items, currentInvoice.tax]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const handleDownloadPDF = async () => {
    const element = componentRef.current;
    const canvas = await html2canvas(element);
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF();
    const imgWidth = 210;
    const pageHeight = 295;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;

    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(`invoice-${currentInvoice.invoiceNumber}.pdf`);
  };

  const addItem = () => {
    setCurrentInvoice(prev => ({
      ...prev,
      items: [...prev.items, { id: uuidv4(), description: '', quantity: 1, price: 0, total: 0 }],
    }));
  };

  const updateItem = (id, field, value) => {
    setCurrentInvoice(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.id === id
          ? {
              ...item,
              [field]: value,
              total: field === 'quantity' || field === 'price' ? value * (item.price || 0) : (item.quantity || 0) * value,
            }
          : item
      ),
    }));
  };

  const removeItem = id => {
    setCurrentInvoice(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== id),
    }));
  };

  const saveInvoice = () => {
    if (!currentInvoice.billTo.name || currentInvoice.items.some(i => !i.description)) {
      alert('Please fill in required fields.');
      return;
    }

    const newInvoice = { ...currentInvoice, id: isEdit ? currentInvoice.id : uuidv4() };
    if (isEdit) {
      setInvoices(prev => prev.map(inv => (inv.id === currentInvoice.id ? newInvoice : inv)));
    } else {
      setInvoices(prev => [...prev, newInvoice]);
    }
    resetForm();
  };

  const loadInvoice = invoice => {
    setCurrentInvoice(invoice);
    setIsEdit(true);
  };

  const deleteInvoice = id => {
    setInvoices(prev => prev.filter(inv => inv.id !== id));
  };

  const resetForm = () => {
    setCurrentInvoice({
      id: '',
      invoiceNumber: '',
      date: new Date().toISOString().split('T')[0],
      dueDate: '',
      billTo: { name: '', address: '', email: '', phone: '' },
      items: [{ id: uuidv4(), description: '', quantity: 1, price: 0, total: 0 }],
      notes: '',
      subtotal: 0,
      tax: 0,
      total: 0,
    });
    setIsEdit(false);
  };

  return (
    <div className="invoice-system">
      <h2>Invoice System</h2>

      <div className="invoice-form">
        {/* Invoice Header */}
        <div className="form-row">
          <div>
            <label>Invoice #</label>
            <input type="text" value={currentInvoice.invoiceNumber} readOnly />
          </div>
          <div>
            <label>Date</label>
            <input
              type="date"
              value={currentInvoice.date}
              onChange={e => setCurrentInvoice(prev => ({ ...prev, date: e.target.value }))}
            />
          </div>
          <div>
            <label>Due Date</label>
            <input
              type="date"
              value={currentInvoice.dueDate}
              onChange={e => setCurrentInvoice(prev => ({ ...prev, dueDate: e.target.value }))}
            />
          </div>
        </div>

        {/* Bill To */}
        <div className="section">
          <h3>Bill To</h3>
          <input
            placeholder="Client Name *"
            value={currentInvoice.billTo.name}
            onChange={e => setCurrentInvoice(prev => ({
              ...prev,
              billTo: { ...prev.billTo, name: e.target.value }
            }))}
          />
          <input
            placeholder="Address"
            value={currentInvoice.billTo.address}
            onChange={e => setCurrentInvoice(prev => ({
              ...prev,
              billTo: { ...prev.billTo, address: e.target.value }
            }))}
          />
          <input
            placeholder="Email"
            value={currentInvoice.billTo.email}
            onChange={e => setCurrentInvoice(prev => ({
              ...prev,
              billTo: { ...prev.billTo, email: e.target.value }
            }))}
          />
          <input
            placeholder="Phone"
            value={currentInvoice.billTo.phone}
            onChange={e => setCurrentInvoice(prev => ({
              ...prev,
              billTo: { ...prev.billTo, phone: e.target.value }
            }))}
          />
        </div>

        {/* Items */}
        <div className="section">
          <h3>Items</h3>
          <table className="items-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Total</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {currentInvoice.items.map(item => (
                <tr key={item.id}>
                  <td>
                    <input
                      value={item.description}
                      onChange={e => updateItem(item.id, 'description', e.target.value)}
                      placeholder="Item description"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={e => updateItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.price}
                      onChange={e => updateItem(item.id, 'price', parseFloat(e.target.value) || 0)}
                    />
                  </td>
                  <td>${item.total.toFixed(2)}</td>
                  <td>
                    <button type="button" onClick={() => removeItem(item.id)} className="remove-btn">
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={addItem} className="add-item-btn">+ Add Item</button>
        </div>

        {/* Totals */}
        <div className="totals">
          <div>
            <label>Tax (%)</label>
            <input
              type="number"
              min="0"
              value={currentInvoice.tax}
              onChange={e => setCurrentInvoice(prev => ({ ...prev, tax: parseFloat(e.target.value) || 0 }))}
            />
          </div>
          <div className="summary">
            <p>Subtotal: ${currentInvoice.subtotal.toFixed(2)}</p>
            <p>Tax: ${(currentInvoice.subtotal * (currentInvoice.tax / 100)).toFixed(2)}</p>
            <p className="total">Total: ${currentInvoice.total.toFixed(2)}</p>
          </div>
        </div>

        {/* Notes */}
        <div className="section">
          <label>Notes</label>
          <textarea
            value={currentInvoice.notes}
            onChange={e => setCurrentInvoice(prev => ({ ...prev, notes: e.target.value }))}
            rows="3"
          />
        </div>

        {/* Actions */}
        <div className="actions">
          <button onClick={saveInvoice} className="save-btn">
            {isEdit ? 'Update' : 'Save'} Invoice
          </button>
          <button onClick={handlePrint} className="print-btn">Print</button>
          <button onClick={handleDownloadPDF} className="download-btn">Download PDF</button>
          <button onClick={resetForm} className="reset-btn">New Invoice</button>
        </div>
      </div>

      {/* Invoice List */}
      <div className="invoice-list">
        <h3>Saved Invoices</h3>
        {invoices.length === 0 ? (
          <p>No invoices saved yet.</p>
        ) : (
          <ul>
            {invoices.map(inv => (
              <li key={inv.id}>
                <span>
                  {inv.invoiceNumber} - {inv.billTo.name} - ${inv.total.toFixed(2)}
                </span>
                <div>
                  <button onClick={() => loadInvoice(inv)}>Edit</button>
                  <button onClick={() => deleteInvoice(inv.id)} className="delete-btn">Delete</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Hidden Printable Invoice */}
      <div style={{ display: 'none' }}>
        <div ref={componentRef} className="printable-invoice">
          <div className="print-header">
            <h1>INVOICE</h1>
            <div>
              <p><strong>{currentInvoice.invoiceNumber}</strong></p>
              <p>Date: {currentInvoice.date}</p>
              {currentInvoice.dueDate && <p>Due: {currentInvoice.dueDate}</p>}
            </div>
          </div>

          <div className="print-billto">
            <h3>Bill To:</h3>
            <p>{currentInvoice.billTo.name}</p>
            {currentInvoice.billTo.address && <p>{currentInvoice.billTo.address}</p>}
            {currentInvoice.billTo.email && <p>{currentInvoice.billTo.email}</p>}
            {currentInvoice.billTo.phone && <p>{currentInvoice.billTo.phone}</p>}
          </div>

          <table className="print-items-table" style={{ width: '100%', margin: '20px 0' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Description</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {currentInvoice.items.map(item => (
                <tr key={item.id}>
                  <td>{item.description}</td>
                  <td>{item.quantity}</td>
                  <td>${item.price.toFixed(2)}</td>
                  <td>${item.total.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="print-totals" style={{ textAlign: 'right' }}>
            <p>Subtotal: ${currentInvoice.subtotal.toFixed(2)}</p>
            <p>Tax ({currentInvoice.tax}%): ${(currentInvoice.subtotal * (currentInvoice.tax / 100)).toFixed(2)}</p>
            <p><strong>Total: ${currentInvoice.total.toFixed(2)}</strong></p>
          </div>

          {currentInvoice.notes && (
            <div className="print-notes">
              <h4>Notes:</h4>
              <p>{currentInvoice.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceSystem;